import { useState, useRef, useEffect } from "react";
import { useQueryClient, useMutation } from "react-query";
import { useTranslation } from 'react-i18next';
import { Modal, ModalSize } from "@components/new/Modal";
import Stepper, { EStepState } from "@components/new/Stepper";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import { Alert, AlertType } from "@components/new/Alert";
import { useProfile } from "@contexts/ProfileContext";
import {
  sendTopUpBalance,
  sendTopUpBalanceResponse,
  checkBalanceReplenishment,
  checkBalanceReplenishmentResponse,
} from "@api/apiClient/finance";
import { createFBPPickupRequest, IFBPPickupRequestData } from "@api/apiClient/fbp";
import Form from "./Form";
import TopUp from "./TopUp";
import { FormHandle } from "./types";
import styles from "./PickUpModal.module.scss";

interface OrderModalProps {
  onClose: () => void;
  restockRequestId: string;
  fbpWarehouseId: string;
}

interface ISubmitButtonProps {
  disabled: boolean;
  isLoading?: boolean;
  isAddressInvalid?: boolean;
  isInsufficient?: boolean;
  missingAmount?: number;
  currency?: string;
}

enum steps {
  FORM = "form",
  TOPUP = "topup",
  SUCCESS = "success",
  ERROR = "error",
}

const PickUpModal = ({ onClose, restockRequestId, fbpWarehouseId }: OrderModalProps) => {
  const [step, setStep] = useState(steps.FORM);
  const [buttonProps, setButtonProps] = useState<ISubmitButtonProps>({
    disabled: false,
    isLoading: false,
  });
  const [topUpData, setTopUpData] = useState<sendTopUpBalanceResponse | undefined>(undefined);
  const [paymentInProcessed, setPaymentInProcessed] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<string | undefined>(undefined);
  // Зафиксированный признак платного (3-шагового) сценария: после ухода в пополнение
  // баланс обновляется и isInsufficient становится false — степпер не должен схлопываться.
  const [enteredTopUp, setEnteredTopUp] = useState<boolean>(false);

  const { t } = useTranslation();
  const formRef = useRef<FormHandle>({ submit: () => {} });
  const payloadRef = useRef<IFBPPickupRequestData | undefined>(undefined);
  const timeoutCheckBalance = useRef<number | null>(null);
  const timeoutCheckPayment = useRef<number | null>(null);
  const queryClient = useQueryClient();
  const { get, account } = useProfile();
  // При бесплатном пикапе (Feature FreeFbpPickUp) не показываем, что пикап платный:
  // кнопка «Оформить» вместо «Оплатить».
  const isFreeFbpPickUp = Boolean(account?.features?.freeFbpPickUp);

  const handleStatusChange = (props: ISubmitButtonProps) => {
    setButtonProps({
      disabled: props.disabled ?? false,
      isLoading: props.isLoading ?? false,
      isAddressInvalid: props.isAddressInvalid ?? false,
      isInsufficient: props.isInsufficient ?? false,
      missingAmount: props.missingAmount ?? 0,
      currency: props.currency ?? "CNY",
    });
  }

  const createPickUpMutation = useMutation({
    mutationFn: createFBPPickupRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getFBPRequests"] });
      setStep(steps.SUCCESS);
    },
    onError: () => setStep(steps.ERROR),
  });

  const { mutate: mutateCheckBalanceReplenishment } = useMutation({
    mutationFn: checkBalanceReplenishment,
  });

  const {
    isLoading: isLoadingSendTopUpBalance,
    mutate: mutateSendTopUpBalance,
  } = useMutation({
    mutationFn: sendTopUpBalance,
    onSuccess: (result: sendTopUpBalanceResponse) => {
      setTopUpData(result);
      setPaymentTimeout();
      waitingBalanceReplenished(result.outTradeNo);
    },
  });

  const clearTimers = () => {
    clearTimeout(timeoutCheckBalance.current as number);
    clearTimeout(timeoutCheckPayment.current as number);
  };

  // На вкладке/анмаунте чистим таймеры опроса.
  useEffect(() => () => clearTimers(), []);

  const setPaymentTimeout = (): void => {
    timeoutCheckPayment.current = window.setTimeout(() => {
      clearTimeout(timeoutCheckBalance.current as number);
      setPaymentInProcessed(false);
    }, 600000);
  };

  const onPaymentSuccess = (): void => {
    clearTimers();
    get(); // обновляем баланс кошелька
    if (payloadRef.current) createPickUpMutation.mutate(payloadRef.current);
  };

  const waitingBalanceReplenished = (id: string): void => {
    timeoutCheckBalance.current = window.setTimeout(() => {
      mutateCheckBalanceReplenishment(id, {
        onSuccess: (data: checkBalanceReplenishmentResponse) => {
          if (data.isProcess) {
            if (!paymentInProcessed) setPaymentInProcessed(true);
            waitingBalanceReplenished(id);
          } else {
            if (paymentInProcessed) setPaymentInProcessed(false);
            if (data.isSuccess) {
              onPaymentSuccess();
            } else if (data.errorCode) {
              clearTimeout(timeoutCheckPayment.current as number);
              setPaymentError(data.errorCode);
            } else {
              waitingBalanceReplenished(id);
            }
          }
        },
      });
    }, 3000);
  };

  const onCloseTopUp = (): void => {
    clearTimers();
    setPaymentInProcessed(false);
    setPaymentError(undefined);
    setTopUpData(undefined);
    setStep(steps.FORM);
  };

  const startTopUp = (): void => {
    setPaymentError(undefined);
    setPaymentInProcessed(false);
    setTopUpData(undefined);
    mutateSendTopUpBalance({ amount: buttonProps.missingAmount ?? 0 });
  };

  // Form отдаёт валидный payload. Хватает средств — создаём заявку сразу;
  // не хватает — переходим к пополнению (QR), а заявку создаём после успешной оплаты.
  const handleSubmitPayload = (p: IFBPPickupRequestData): void => {
    payloadRef.current = p;
    if (buttonProps.isInsufficient) {
      setEnteredTopUp(true);
      setStep(steps.TOPUP);
      startTopUp();
    } else {
      createPickUpMutation.mutate(p);
    }
  };

  const lastStepState = (
    step === steps.SUCCESS ? EStepState.SUCCESS : (
      step === steps.ERROR ? EStepState.ERROR : EStepState.DISABLED
    )
  );

  // Платный сценарий — пока на форме определяется живым isInsufficient, а после ухода
  // в пополнение фиксируется через enteredTopUp (иначе после оплаты степпер схлопнулся бы).
  const threeStep = buttonProps.isInsufficient || enteredTopUp;

  // Состояние шага «Пополнение»: активен на самом шаге, пройден (SUCCESS) на завершении/ошибке.
  const topUpStepState = step === steps.TOPUP
    ? EStepState.ENABLED
    : (step === steps.SUCCESS || step === steps.ERROR)
      ? EStepState.SUCCESS
      : EStepState.DISABLED;

  // Степпер динамический: при нехватке средств появляется промежуточный шаг «Пополнение».
  const stepsData = threeStep ? [
    { text: t('fbpPage.pickUp.new.step-1'), state: EStepState.ENABLED },
    {
      text: t('fbpPage.pickUp.new.step-topup'),
      state: topUpStepState,
    },
    { text: t('fbpPage.pickUp.new.step-3'), state: lastStepState },
  ] : [
    { text: t('fbpPage.pickUp.new.step-1'), state: EStepState.ENABLED },
    { text: t('fbpPage.pickUp.new.step-3'), state: lastStepState },
  ];

  // 3 шага раскладываем на всю ширину, 2 — компактно.
  const stepperGap = threeStep ? "auto" : "small";

  if (step === steps.SUCCESS) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.pickUp.title')}
        headerContent={<Stepper gap={stepperGap} steps={stepsData} />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.pickUp.closeBtn'),
            onClick: onClose,
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.pickUp.successTitle')}
          description={t('fbpPage.pickUp.successDescription')}
          variant="success"
        />
      </Modal>
    )
  }

  if (step === steps.ERROR) {
    return (
      <Modal
        isOpen={true}
        title={t('fbpPage.pickUp.title')}
        headerContent={<Stepper gap={stepperGap} steps={stepsData} />}
        size={ModalSize.L}
        onClose={onClose}
        footerButtons={[
          {
            title: t('fbpPage.pickUp.closeBtn'),
            onClick: onClose,
          },
          {
            title: t('fbpPage.pickUp.repeatBtn'),
            onClick: () => setStep(steps.FORM),
          },
        ]}
      >
        <MessageModalContent
          title={t('fbpPage.pickUp.errorTitle')}
          description={t('fbpPage.pickUp.errorDescription')}
          variant="warning"
        />
      </Modal>
    )
  }

  const isTopUp = step === steps.TOPUP;

  // FORM и TOPUP в одной Modal: Form не размонтируется при переходе на пополнение,
  // поэтому при «Назад» введённые данные сохраняются. На TOPUP форма просто скрыта.
  return (
    <Modal
      isOpen={true}
      title={t('fbpPage.pickUp.title')}
      headerContent={<Stepper gap={stepperGap} steps={stepsData} />}
      headerTitleContent={!isTopUp && buttonProps.isAddressInvalid ? (
        <div className={styles.validationAlert}>
          <Alert
            type={AlertType.ERROR}
            title={t("fbpPage.pickUp.addressValidationErrorDescription")}
          />
        </div>
      ) : null}
      size={ModalSize.L}
      onClose={onClose}
      confirmCancel
      footerButtons={isTopUp ? [
        {
          title: t('fbpPage.pickUp.backBtn'),
          onClick: onCloseTopUp,
        },
      ] : [
        {
          title: t('fbpPage.pickUp.cancelBtn'),
        },
        buttonProps.isInsufficient ? {
          title: t('fbpPage.pickUp.topUpBtn'),
          onClick: () => formRef.current && formRef.current.submit(),
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading || isLoadingSendTopUpBalance,
        } : {
          title: isFreeFbpPickUp ? t('fbpPage.pickUp.createBtn') : t('fbpPage.pickUp.payBtn'),
          onClick: () => formRef.current && formRef.current.submit(),
          disabled: buttonProps.disabled,
          loading: buttonProps.isLoading || createPickUpMutation.isLoading,
        },
      ]}
    >
      <div className={isTopUp ? styles.hidden : styles.formWrap}>
        <Form
          formRef={formRef}
          restockRequestId={restockRequestId}
          fbpWarehouseId={fbpWarehouseId}
          onStatusChange={handleStatusChange}
          onSubmitPayload={handleSubmitPayload}
        />
      </div>
      {isTopUp ? (
        <TopUp
          qrCode={topUpData?.qrCode}
          isProcessing={paymentInProcessed || createPickUpMutation.isLoading}
          isLoading={isLoadingSendTopUpBalance}
          error={paymentError}
          onRefresh={startTopUp}
        />
      ) : null}
    </Modal>
  )
}

export default PickUpModal;
